import { useCallback, useState } from "react";
import { parseDateTimeOrNull } from "@/lib/date-utils";
import { parseApiError } from "@/lib/error-handling";
import { buildUpdateEventPayloadFromEvent } from "@/lib/event-utils";
import { useAdminEventUpdate } from "@/lib/admin-api";
import { confirmAsync, type ToastMessage } from "@/lib/ui";
import type { AdminEvent, AdminEventPricePoint } from "../types";

type PricePointDraft = {
  label: string;
  description: string;
  priceCents: number;
  currency: string;
  sortOrder: number;
  availableFrom: Date | null;
  availableUntil: Date | null;
  depositCents: number | null;
  balanceDueAt: Date | null;
  capacity: number | null;
};

type UseEventPricePointsArgs = {
  event: AdminEvent | null;
  eventId: string | undefined;
  refreshEvent: () => Promise<void>;
  showToast: (message: ToastMessage) => void;
};

export function useEventPricePoints(args: UseEventPricePointsArgs) {
  const { event, eventId, refreshEvent, showToast } = args;
  const updateMutation = useAdminEventUpdate();

  const [pricePointDialogVisible, setPricePointDialogVisible] = useState(false);
  const [editingPricePoint, setEditingPricePoint] =
    useState<AdminEventPricePoint | null>(null);
  const [newPricePoint, setNewPricePoint] = useState<PricePointDraft>({
    label: "",
    description: "",
    priceCents: 0,
    currency: "gbp",
    sortOrder: 0,
    availableFrom: null,
    availableUntil: null,
    depositCents: null,
    balanceDueAt: null,
    capacity: null,
  });

  const handleMovePricePointUp = useCallback(
    async (pricePointId: string) => {
      if (!event || !eventId) return;

      const sortedPricePoints = [...event.pricePoints].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      const currentIndex = sortedPricePoints.findIndex(
        (pp) => pp.id === pricePointId,
      );

      if (currentIndex === -1 || currentIndex === 0) return;

      const currentPP = sortedPricePoints[currentIndex];
      const previousPP = sortedPricePoints[currentIndex - 1];

      const updatedPricePoints = event.pricePoints.map((pp) => {
        if (pp.id === currentPP.id) {
          return { ...pp, sortOrder: previousPP.sortOrder };
        }
        if (pp.id === previousPP.id) {
          return { ...pp, sortOrder: currentPP.sortOrder };
        }
        return pp;
      });

      try {
        const payload = buildUpdateEventPayloadFromEvent(event, {
          pricePoints: updatedPricePoints,
        });

        await updateMutation.mutateAsync({
          params: { path: { eventId } },
          body: payload,
        });

        showToast({
          severity: "success",
          summary: "Success",
          detail: "Price point moved up successfully",
        });
        await refreshEvent();
      } catch (error) {
        showToast({
          severity: "error",
          summary: "Error",
          detail:
            error instanceof Error
              ? error.message
              : "Failed to move price point up",
        });
      }
    },
    [event, eventId, refreshEvent, showToast, updateMutation],
  );

  const handleMovePricePointDown = useCallback(
    async (pricePointId: string) => {
      if (!event || !eventId) return;

      const sortedPricePoints = [...event.pricePoints].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      const currentIndex = sortedPricePoints.findIndex(
        (pp) => pp.id === pricePointId,
      );

      if (
        currentIndex === -1 ||
        currentIndex === sortedPricePoints.length - 1
      ) {
        return;
      }

      const currentPP = sortedPricePoints[currentIndex];
      const nextPP = sortedPricePoints[currentIndex + 1];

      const updatedPricePoints = event.pricePoints.map((pp) => {
        if (pp.id === currentPP.id) {
          return { ...pp, sortOrder: nextPP.sortOrder };
        }
        if (pp.id === nextPP.id) {
          return { ...pp, sortOrder: currentPP.sortOrder };
        }
        return pp;
      });

      try {
        const payload = buildUpdateEventPayloadFromEvent(event, {
          pricePoints: updatedPricePoints,
        });

        await updateMutation.mutateAsync({
          params: { path: { eventId } },
          body: payload,
        });

        showToast({
          severity: "success",
          summary: "Success",
          detail: "Price point moved down successfully",
        });
        await refreshEvent();
      } catch (error) {
        showToast({
          severity: "error",
          summary: "Error",
          detail:
            error instanceof Error
              ? error.message
              : "Failed to move price point down",
        });
      }
    },
    [event, eventId, refreshEvent, showToast, updateMutation],
  );

  const handleAddPricePoint = useCallback(() => {
    setEditingPricePoint(null);
    setNewPricePoint({
      label: "",
      description: "",
      priceCents: 0,
      currency: "gbp",
      sortOrder: event?.pricePoints.length || 0,
      availableFrom: null,
      availableUntil: null,
      depositCents: null,
      balanceDueAt: null,
      capacity: null,
    });
    setPricePointDialogVisible(true);
  }, [event?.pricePoints.length]);

  const handleEditPricePoint = useCallback(
    (pricePoint: AdminEventPricePoint) => {
      setEditingPricePoint(pricePoint);
      setNewPricePoint({
        label: pricePoint.label,
        description: pricePoint.description || "",
        priceCents: pricePoint.priceCents,
        currency: "gbp",
        sortOrder: pricePoint.sortOrder,
        availableFrom: parseDateTimeOrNull(pricePoint.availableFrom),
        availableUntil: parseDateTimeOrNull(pricePoint.availableUntil),
        depositCents: pricePoint.depositCents ?? null,
        balanceDueAt: parseDateTimeOrNull(pricePoint.balanceDueAt),
        capacity: pricePoint.capacity ?? null,
      });
      setPricePointDialogVisible(true);
    },
    [],
  );

  const performRemovePricePoint = useCallback(
    async (pricePointId: string) => {
      if (!event || !eventId) return;

      const updatedPricePoints = event.pricePoints.filter(
        (pp) => pp.id !== pricePointId,
      );

      try {
        const payload = buildUpdateEventPayloadFromEvent(event, {
          pricePoints: updatedPricePoints,
        });

        try {
          await updateMutation.mutateAsync({
            params: { path: { eventId } },
            body: payload,
          });
        } catch (apiError: unknown) {
          const { message } = parseApiError(apiError);
          throw new Error(message || "Failed to remove price point");
        }

        showToast({
          severity: "success",
          summary: "Success",
          detail: "Price point removed successfully",
        });

        await refreshEvent();
      } catch (error) {
        let errorDetail = "Failed to remove price point";
        if (error instanceof Error) {
          errorDetail = error.message;
        } else if (typeof error === "object" && error !== null) {
          errorDetail = JSON.stringify(error);
        } else if (typeof error === "string") {
          errorDetail = error;
        }

        showToast({
          severity: "error",
          summary: "Error",
          detail: errorDetail,
        });
      }
    },
    [event, eventId, refreshEvent, showToast, updateMutation],
  );

  const confirmAndRemovePricePoint = useCallback(
    async (pricePointId: string, label: string) => {
      const confirmed = await confirmAsync({
        message: `Are you sure you want to delete "${label}"?`,
        header: "Confirm Deletion",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        defaultFocus: "reject",
      });
      if (!confirmed) return;
      await performRemovePricePoint(pricePointId);
    },
    [performRemovePricePoint],
  );

  const handleRemovePricePoint = useCallback(
    (pricePointId: string) => {
      if (!event || !eventId) return;

      const updatedPricePoints = event.pricePoints.filter(
        (pp) => pp.id !== pricePointId,
      );
      if (updatedPricePoints.length === 0) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Event must have at least one price point",
        });
        return;
      }

      const pricePoint = event.pricePoints.find((pp) => pp.id === pricePointId);
      const pricePointLabel = pricePoint?.label || "this price point";

      void confirmAndRemovePricePoint(pricePointId, pricePointLabel);
    },
    [confirmAndRemovePricePoint, event, eventId, showToast],
  );

  const handleSavePricePoint = useCallback(async () => {
    if (!event || !eventId) return;

    const labelTrimmed = newPricePoint.label?.trim();
    if (!labelTrimmed) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Label is required",
      });
      return;
    }

    if (newPricePoint.priceCents < 0) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Price cannot be negative",
      });
      return;
    }

    if (newPricePoint.depositCents !== null && newPricePoint.depositCents !== undefined) {
      if (newPricePoint.depositCents > newPricePoint.priceCents - 500) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Deposit must be at least £5 less than total price",
        });
        return;
      }

      if (!newPricePoint.balanceDueAt) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Balance due date is required if a deposit is set",
        });
        return;
      }

      const sessionStartTimes = event.sessions
        .map((s) => parseDateTimeOrNull(s.startsAt))
        .filter((d): d is Date => d != null)
        .map((d) => d.getTime());

      const eventStart = parseDateTimeOrNull(event.startsAt);
      const earliestStartTime =
        sessionStartTimes.length > 0
          ? Math.min(...sessionStartTimes)
          : eventStart?.getTime();

      if (earliestStartTime == null) {
        showToast({
          severity: "error",
          summary: "Error",
          detail:
            "Cannot validate balance due date because event start date is missing",
        });
        return;
      }

      const earliestSessionStart = new Date(earliestStartTime);

      if (newPricePoint.balanceDueAt.getTime() > earliestSessionStart.getTime()) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Balance due date must be on or before event start date",
        });
        return;
      }
    }

    try {
      let updatedPricePoints = [...event.pricePoints];
      if (editingPricePoint) {
        const index = updatedPricePoints.findIndex(
          (pp) => pp.id === editingPricePoint.id,
        );
        if (index >= 0) {
          updatedPricePoints[index] = {
            ...updatedPricePoints[index],
            label: labelTrimmed,
            description: newPricePoint.description || null,
            priceCents: newPricePoint.priceCents,
            currency: "gbp",
            sortOrder: newPricePoint.sortOrder,
            availableFrom: newPricePoint.availableFrom?.toISOString() || null,
            availableUntil: newPricePoint.availableUntil?.toISOString() || null,
            depositCents: newPricePoint.depositCents,
            balanceDueAt: newPricePoint.balanceDueAt?.toISOString() || null,
            capacity: newPricePoint.capacity,
          };
        }
      } else {
        updatedPricePoints.push({
          id: "",
          eventId: event.id,
          label: labelTrimmed,
          description: newPricePoint.description || null,
          priceCents: newPricePoint.priceCents,
          currency: "gbp",
          sortOrder: newPricePoint.sortOrder,
          availableFrom: newPricePoint.availableFrom?.toISOString() || null,
          availableUntil: newPricePoint.availableUntil?.toISOString() || null,
          depositCents: newPricePoint.depositCents,
          balanceDueAt: newPricePoint.balanceDueAt?.toISOString() || null,
          capacity: newPricePoint.capacity,
          createdAt: null,
          updatedAt: null,
        });
      }

      const payload = buildUpdateEventPayloadFromEvent(event, {
        pricePoints: updatedPricePoints,
      });

      try {
        await updateMutation.mutateAsync({
          params: { path: { eventId } },
          body: payload,
        });
      } catch (apiError: unknown) {
        const { message } = parseApiError(apiError);
        throw new Error(message || "Failed to save price point");
      }

      showToast({
        severity: "success",
        summary: "Success",
        detail: editingPricePoint
          ? "Price point updated successfully"
          : "Price point added successfully",
      });

      setPricePointDialogVisible(false);
      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail:
          error instanceof Error ? error.message : "Failed to save price point",
      });
    }
  }, [editingPricePoint, event, eventId, newPricePoint, refreshEvent, showToast, updateMutation]);

  return {
    pricePointDialogVisible,
    setPricePointDialogVisible,
    editingPricePoint,
    newPricePoint,
    setNewPricePoint,
    handleMovePricePointUp,
    handleMovePricePointDown,
    handleAddPricePoint,
    handleEditPricePoint,
    handleRemovePricePoint,
    handleSavePricePoint,
  };
}

